import { useState, useContext, useCallback, useEffect, useMemo } from 'react';

import cn from 'classnames';
import * as Dfns from 'date-fns/fp';

import { MdKeyboardTab } from 'react-icons/md';

import { Modal, ListGroup } from 'react-bootstrap';

import { Button } from '../../Components/Button';
import { Checkbox } from '../../Components/FormCheck';
import { Appearance, Design, Intent } from '../../Components/designConstants';

import { StreamingOrdersLambdaFetch, awaitJSON } from '../../utils/fetch-utils';
import { useLocationCompany } from '../../utils/hooks/useLocationCompany';
import { useSetError } from '../../redux/modals';

import { ListGroupSkeleton, Skeleton } from '../../Components/Skeleton';
import { Spinner } from '../../Components/Spinner';
import { DateBox } from '../../Components/DateBox';

import { OrderViewContext, toPrettyImpressions, toPrettySpendWithCents } from './order-view-utils';
import { Flight, NetworkOrder } from './streaming-buying-types';
import { useCompanyOrders } from './use-streaming-buying';

import './MoveFlightModal.scss';

interface MoveFlightModalProps {
  flight: Flight;
  onClose: () => void;
  onSave: () => void;
}

const makePrettyDate = (date: string) => Dfns.format('M/dd/yyyy', Dfns.parseISO(date));

export const MoveFlightModal = ({ flight, onClose, onSave }: MoveFlightModalProps) => {
  const setError = useSetError();
  const company = useLocationCompany();

  const { order, network } = useContext(OrderViewContext);

  const { data: allOrders, error: ordersError } = useCompanyOrders(company ?? '');

  // Only orders on this network are valid move targets, and never the one we're moving from.
  const orders = useMemo<NetworkOrder[] | undefined>(
    () =>
      allOrders &&
      (allOrders[network] ?? [])
        .filter(({ id }) => id !== order.id)
        .sort((a, b) => b.start_date.localeCompare(a.start_date)),
    [allOrders, network, order.id]
  );

  useEffect(() => {
    if (ordersError) {
      setError({
        message: ordersError.message,
        reportError: ordersError
      });
    }
  }, [ordersError, setError]);

  const [showHelp, setShowHelp] = useState(false);

  const [selectedOrder, setSelectedOrder] = useState<number | 'new' | null>(null);

  const [saving, setSaving] = useState(false);

  const [applyToSubsequent, setApplyToSubsequent] = useState(false);
  const [showSubsequentHelp, setShowSubsequentHelp] = useState(false);

  const descriptionDisplay = useMemo(() => {
    const partsToDisplay: string[] = [];
    if (flight.showTitle) {
      partsToDisplay.push(flight.showTitle);
    }
    if (flight.spotPosition) {
      partsToDisplay.push(flight.spotPosition);
    }
    if (flight.description) {
      partsToDisplay.push(flight.description);
    }
    return partsToDisplay.join(' | ');
  }, [flight]);

  const ourOnSave = useCallback(async () => {
    setSaving(true);
    try {
      const res = await StreamingOrdersLambdaFetch('/orders/move_flight', {
        method: 'post',
        body: {
          id: flight.id,
          orderID: selectedOrder,
          applyToSubsequent
        }
      });
      const { newOrderID } = await awaitJSON(res);
      await setError({
        intent: Intent.SUCCESS,
        title: 'Successfully Moved Flight',
        message: (
          <p>
            Successfully moved flight to order{' '}
            <a target='_blank' rel='noopener noreferrer' href={`./${newOrderID}`}>
              #{newOrderID}
            </a>
            . Make sure you send the updated versions of this and that flight.
          </p>
        )
      });
      onSave();
    } catch (e) {
      setError({
        message: `Failed to move flight. Error: ${e.message}`,
        reportError: e
      });
      setSaving(false);
    }
  }, [onSave, setError, flight, selectedOrder, applyToSubsequent]);

  const ourOnClose = useCallback(() => {
    if (saving) {
      setError({
        message: "You can't close until the flight is moved."
      });
    } else {
      onClose();
    }
  }, [saving, setError, onClose]);
  return (
    <Modal show onHide={ourOnClose} className='streaming-buying-move-flight-modal'>
      <Modal.Header closeButton>
        <Modal.Title className='header'>
          <MdKeyboardTab className='header-icon' />
          <span>Move Flight #{flight.id}</span>
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className='flight-info'>
          <div>
            <div className='labeled-item'>
              <span className='label'>Spend:</span>
              <span>{toPrettySpendWithCents(flight.spend)}</span>
            </div>
            <div className='labeled-item'>
              <span className='label'>CPM:</span>
              <span>{toPrettySpendWithCents(flight.cpm)}</span>
            </div>
            <div className='labeled-item'>
              <span className='label'>Imps:</span>
              <span>{toPrettyImpressions(flight.impressions)}</span>
            </div>
          </div>
          <div>
            <div className='labeled-item'>
              <span className='label'>Description:</span>
              <span>{descriptionDisplay}</span>
            </div>
          </div>
          <div>
            <div className='labeled-item'>
              <span className='label'>Dates:</span>
              <DateBox start={flight.startDate} end={flight.endDate} />
            </div>
          </div>
        </div>

        <div className='apply-to-subsequent'>
          <Checkbox
            id='applyToSubsequentCheckbox'
            label='Apply To Subsequent Flights'
            checked={applyToSubsequent}
            onChange={e => setApplyToSubsequent(e.target.checked)}
          />
          <Button
            appearance={Appearance.EMPTY}
            className='help-button'
            onClick={() => setShowSubsequentHelp(v => !v)}
          >
            What does this mean?
          </Button>
        </div>

        {showSubsequentHelp && (
          <div className='help'>
            <div>
              When you check this box, in addition to moving the flight you&apos;ve selected, any
              flight in this IO that occurs after will be automatically canceled, and they will be
              assigned the new canceled derived network.
              <br />
              <br />
              Do this if you want to change from a weekly to a monthly IO. This lets you change the
              end date of the moved flight in the new IO to overlap with the subsequent flights.
            </div>
          </div>
        )}

        <div
          className={cn('order-list-container', { loading: !orders })}
          data-testid='move-flight-order-list'
        >
          {orders ? (
            <ListGroup>
              <ListGroup.Item
                action
                active={selectedOrder === 'new'}
                onClick={() => setSelectedOrder(id => (id === 'new' ? null : 'new'))}
              >
                New Order
              </ListGroup.Item>
              {orders.map(({ id, start_date, end_date }) => (
                <ListGroup.Item
                  action
                  key={id}
                  data-testid={`move-target-order-${id}`}
                  className='order-row'
                  active={selectedOrder === id}
                  onClick={() => setSelectedOrder(selectedID => (selectedID === id ? null : id))}
                >
                  <div>Order ID #{id}</div>
                  <div>{makePrettyDate(start_date)}</div>
                  <div>{makePrettyDate(end_date)}</div>
                </ListGroup.Item>
              ))}
            </ListGroup>
          ) : (
            <Skeleton>
              <ListGroupSkeleton />
            </Skeleton>
          )}
        </div>
        <Button
          appearance={Appearance.EMPTY}
          className='help-button'
          onClick={() => setShowHelp(v => !v)}
        >
          What happens when I do this?
        </Button>
        {showHelp && (
          <div className='help'>
            <div>
              Moving this flight to another IO will copy this flight in that IO. It will then take
              this flight, cancel it, and set spend and impressions to 0 (if in the future).
              Internal derived networks will be adjusted so impressions will match to the new,
              copied flight, not the canceled one left in this IO.
              <br />
              <br />
              After moving this flight, you should resend this IO and the IO the flight was moved
              to. The changes will automatically be applied to the database, but will not be
              automatically sent to the networks.
            </div>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Button appearance={Appearance.OUTLINED} design={Design.NEUTRAL} onClick={ourOnClose}>
          Cancel
        </Button>
        <Button
          design={Intent.WARNING}
          disabled={!selectedOrder}
          onClick={ourOnSave}
          className='save-button'
        >
          Move Flight
        </Button>
      </Modal.Footer>
      {saving && (
        <div className='saving-box'>
          <Spinner size={60} />
        </div>
      )}
    </Modal>
  );
};
